import { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  blurDataUrl?: string;
  sizes?: string;
  priority?: boolean;
}

// Responsive breakpoints for srcset
const RESPONSIVE_WIDTHS = [320, 480, 640, 768, 1024, 1280, 1536];

/**
 * Generates responsive image URLs for Shopify CDN images
 * Shopify CDN supports width parameter in query string
 */
const generateShopifySrcSet = (src: string): { srcSet: string; webpSrcSet: string } | null => {
  if (!src.includes('cdn.shopify.com')) {
    return null;
  }

  try {
    const url = new URL(src);
    
    // Generate srcset using width query parameter
    const srcSetEntries = RESPONSIVE_WIDTHS.map(width => {
      const newUrl = new URL(src);
      newUrl.searchParams.set('width', width.toString());
      return `${newUrl.toString()} ${width}w`;
    });

    // Generate WebP srcset
    const webpSrcSetEntries = RESPONSIVE_WIDTHS.map(width => {
      const newUrl = new URL(src);
      newUrl.searchParams.set('width', width.toString());
      newUrl.searchParams.set('format', 'webp');
      return `${newUrl.toString()} ${width}w`;
    });

    return {
      srcSet: srcSetEntries.join(', '),
      webpSrcSet: webpSrcSetEntries.join(', ')
    };
  } catch {
    return null;
  }
};

/**
 * Generates a low-quality placeholder URL for Shopify images
 */
const generatePlaceholderUrl = (src: string): string | null => {
  if (!src.includes('cdn.shopify.com')) {
    return null;
  }

  try {
    const newUrl = new URL(src);
    newUrl.searchParams.set('width', '40');
    newUrl.searchParams.set('quality', '10');
    return newUrl.toString();
  } catch {
    return null;
  }
};

export const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  containerClassName = '',
  blurDataUrl,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  priority = false
}: LazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  // Generate responsive srcsets
  const responsiveData = useMemo(() => generateShopifySrcSet(src), [src]);
  const placeholderUrl = useMemo(() => blurDataUrl || generatePlaceholderUrl(src), [src, blurDataUrl]);

  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const placeholderColor = 'hsl(var(--secondary) / 0.3)';

  return (
    <div 
      ref={imgRef}
      className={cn(
        'relative overflow-hidden bg-secondary/20',
        containerClassName
      )}
    >
      {/* Blur placeholder background */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-500',
          isLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
        )}
        style={{
          backgroundColor: placeholderColor,
        }}
      >
        {placeholderUrl && (
          <img
            src={placeholderUrl}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover blur-lg scale-110"
          />
        )}
        {/* Loading shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
      </div>

      {/* Actual image with responsive srcset */}
      {isInView && (
        responsiveData ? (
          <picture>
            {/* WebP source for modern browsers */}
            <source
              type="image/webp"
              srcSet={responsiveData.webpSrcSet}
              sizes={sizes}
            />
            {/* Fallback to original format */}
            <source
              srcSet={responsiveData.srcSet}
              sizes={sizes}
            />
            <img
              src={src}
              alt={alt}
              loading={priority ? 'eager' : 'lazy'}
              decoding={priority ? 'sync' : 'async'}
              onLoad={() => setIsLoaded(true)}
              className={cn(
                'transition-opacity duration-500',
                isLoaded ? 'opacity-100' : 'opacity-0',
                className
              )}
            />
          </picture>
        ) : (
          <img
            src={src}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            onLoad={() => setIsLoaded(true)}
            className={cn(
              'transition-opacity duration-500',
              isLoaded ? 'opacity-100' : 'opacity-0',
              className
            )}
          />
        )
      )}
    </div>
  );
};
